import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { EASE_OUT } from '@/lib/ease';
import { RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { userAPI } from '../UserAPI';
import '../App.sass';
import { useCustomerTheme } from '../Theme';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';
import { fluentIconComponent } from './fluent-icon-map';
import { MarqueeText } from '../chat/MarqueeText';

import { Button } from '@/components/ui/button';

export interface Suggestion {
    id: string;
    icon: string;
    iconStyles: React.CSSProperties;
    text: string;
    screenshotURL: string;
    /**
     * Das Werkzeug (oder Toolset), aus dem das Beispiel stammt (tri-server `chatbotIdeaRanking.ts`).
     * Der Klick legt es fest, damit die Frage dort landet, wo sie herkommt.
     */
    toolId?: string;
    toolLabel?: string;
}

/** So viele Vorschläge stehen auf einmal da. */
export const SUGGESTIONS_PER_PAGE = 3;

/**
 * Die Vorschläge einer Seite — in der Reihenfolge des Servers, nie gemischt, und **ringförmig**:
 * Die Seite nach der letzten vollen läuft über das Ende hinweg in den Anfang, damit immer drei
 * dastehen. Eine halbe letzte Seite zeigte sonst einen Vorschlag und zwei Ladebalken — das sah aus
 * wie Laden, war aber das Ende. Gemischt wurde bis 06.10.2026 zweimal (Server und hier), und die
 * Rückmeldung war, die Vorschläge wirkten zufällig (Sasse).
 */
export function suggestionPage<T>(items: T[], page: number, size: number = SUGGESTIONS_PER_PAGE): T[] {
    if (items.length <= size) return items.slice();
    const start = (((page * size) % items.length) + items.length) % items.length;
    return Array.from({ length: size }, (_, offset) => items[(start + offset) % items.length]);
}

interface SuggestionsProps {
    onSuggestionClick: (suggestion: Suggestion) => void;
    onClose?: () => void;
    showControls?: boolean;
    refreshIntervalSeconds?: number;
    publicSkills?: boolean;
    /**
     * `cards` (default): three tiles above the composer. `list`: plain rows — icon and text —
     * for the empty chat's start screen under the centred composer (28.09.2026).
     */
    variant?: 'cards' | 'list';
}

const _iconClass = {
    fontSize: 30,
    height: 30,
    width: 30,
};

// Server-issued icons come from a fixed Fluent-name pool (see
// tri-server/src/chatbotIdeas.ts). Resolve to a lucide component via
// fluentIconComponent; fall back to Lightbulb if the name is unknown.
const resolveSuggestionIcon = (iconName: string): React.ComponentType<any> => {
    const Component = fluentIconComponent(iconName);
    if (Component) return Component;
    return fluentIconComponent('Lightbulb')!;
};

/** Relative luminance of a `#rgb`/`#rrggbb`/`rgb(a)` colour, or null when unparseable. */
const luminanceOf = (color: string): number | null => {
    let r: number, g: number, b: number;
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color.trim());
    if (hex) {
        const h = hex[1].length === 3 ? hex[1].split('').map(c => c + c).join('') : hex[1];
        [r, g, b] = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
    } else if (rgb) {
        [r, g, b] = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    } else {
        return null;
    }
    const lin = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

export const FALLBACK_ICON_COLOR = '#666666';

/**
 * An icon colour that reads on the light surface: a theme may carry white or near-white
 * tints (primaryLight, secondaryLight), and a white icon on white is no icon. Anything too
 * bright or unparseable falls back to a mid grey.
 */
export const legibleIconColor = (color: string | undefined | null): string => {
    if (!color) return FALLBACK_ICON_COLOR;
    const l = luminanceOf(color);
    return l === null || l > 0.6 ? FALLBACK_ICON_COLOR : color;
};

export const Suggestions: React.FC<SuggestionsProps> = ({
    onSuggestionClick,
    onClose = () => { },
    showControls = true,
    refreshIntervalSeconds = 10,
    publicSkills = false,
    variant = 'cards',
}) => {
    const [authentication] = useAtom(authenticationAtom) as any;
    const { t } = useTranslation();
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    // A rotation swaps each slot's content in place: the old text lifts and fades, the new one
    // drops in, slot by slot with a small stagger. `popLayout` takes the leaving element out
    // of the flow, so the tiles and rows keep their size while both are on screen.
    const reduceMotion = useReducedMotion() ?? false;
    const swap = (slot: number) => ({
        initial: reduceMotion ? false : { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        exit: reduceMotion ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: -8 },
        transition: reduceMotion ? { duration: 0 } : { duration: 0.26, ease: EASE_OUT, delay: slot * 0.06 },
    });
    const [skills, setSkills] = useState([]);
    // Ob die Antwort da ist — auch eine leere oder gescheiterte. Ohne das standen bei einem
    // Kunden ohne Beispiele (oder nach einem Fehler) dauerhaft drei Platzhalter-Balken da, die wie
    // Laden aussahen.
    const [loaded, setLoaded] = useState(false);
    // While the pointer rests on the suggestions or focus is inside them, the automatic
    // rotation holds: the row being read (or marqueeing) must not be swapped away under the
    // pointer. A ref, so the running interval sees it without being restarted; ticks during
    // the hold are simply skipped.
    const holdRotationRef = useRef(false);
    const [resetSuggestionsTimer, setResetSuggestionsTimer] = useState(true);
    const { theme } = useCustomerTheme();
    
    const colors = [
        theme.primary,
        theme.primaryLight,
        theme.secondary,
        theme.secondaryLight,
        theme.tertiary,
        theme.primaryDark,
        theme.secondaryDark,
        theme.tertiaryDark,
    ].filter(color => color && legibleIconColor(color) === color); // no undefined, nothing near white

    // Welche Dreiergruppe gerade steht. Ein Ref neben dem State, damit das laufende Intervall
    // weiterblättert, ohne neu gestartet zu werden.
    const pageRef = useRef(0);

    const selectSuggestions = (skills: any[], page: number = pageRef.current) => {
        pageRef.current = page;
        const newSuggestions = suggestionPage(skills, page).map((skill: any) => {
            // Die Farbe hängt am Werkzeug, nicht am Zufall: dasselbe Werkzeug, dieselbe Farbe.
            const key = String(skill.toolId ?? skill.id ?? skill.text ?? '');
            let hash = 0;
            for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
            const selectedColor = legibleIconColor(skill.iconColor || (colors.length > 0
                ? colors[hash % colors.length]
                : FALLBACK_ICON_COLOR));

            return {
                ...skill,
                iconColor: selectedColor,
                iconStyles: {},
            };
        });

        setSuggestions(newSuggestions);
    };

    /** „Weitere Vorschläge": die nächste Dreiergruppe, am Ende wieder die erste. */
    const handleRefresh = () => {
        selectSuggestions(skills, pageRef.current + 1);
        setResetSuggestionsTimer(true);
    };

    const handleClose = () => {
        console.log('Close button clicked');
        onClose();
    };

    // Fetch skills on mount and whenever the authenticated session changes.
    // Without the auth dep the list would stay stale across logout/login when
    // the component stays mounted (e.g. jotai atom flip without unmount).
    useEffect(() => {
        if (!publicSkills && !authentication?.customer) {
            setSkills([]);
            setSuggestions([]);
            setLoaded(true);
            return;
        }
        const fetchSkills = async () => {
            try {
                let newSkills = [];

                if (publicSkills) {
                    newSkills = await userAPI.getSkills();
                } else {
                    newSkills = await userAPI.getChatbotIdeas();
                }

                setSkills(newSkills);
                selectSuggestions(newSkills, 0);
            } catch (error) {
                console.error('Error fetching skills:', error);
                setSkills([]);
                setSuggestions([]);
            }
            setLoaded(true);
        };

        fetchSkills();
    }, [publicSkills, authentication?.customer, authentication?.id]);

    // Auto-refresh suggestions based on configured interval
    useEffect(() => {
        if (!resetSuggestionsTimer) {
            return;
        }

        let interval: NodeJS.Timeout;

        // Ohne Intervall stehen die Vorschläge still. Das ist der Normalfall im
        // Chat: Ein Vorschlag, den man gerade liest, soll nicht unter dem Blick
        // gegen einen anderen getauscht werden — neu gemischt wird über den
        // Knopf daneben.
        if (skills.length > 0 && refreshIntervalSeconds > 0) {
            interval = setInterval(() => {
                if (holdRotationRef.current) return;
                selectSuggestions(skills, pageRef.current + 1);
            }, refreshIntervalSeconds * 1000);
        }

        return () => clearInterval(interval);
    }, [skills, resetSuggestionsTimer, refreshIntervalSeconds]);

    // Debug render - removed console logs

    // Nichts zu zeigen ist eine Antwort: dann kein Kasten, keine Balken, kein „Weitere Vorschläge".
    if (loaded && suggestions.length === 0) return null;
    // Platzhalter nur, solange die Antwort unterwegs ist; danach so viele Zeilen wie Vorschläge.
    const slots = loaded ? suggestions.map((_, slot) => slot) : [0, 1, 2];

    return (
        <div
            className="relative z-[9999]"
            onPointerEnter={() => { holdRotationRef.current = true; }}
            onPointerLeave={() => { holdRotationRef.current = false; }}
            onFocus={() => { holdRotationRef.current = true; }}
            onBlur={(event) => {
                // Focus moving between rows stays inside: keep holding.
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    holdRotationRef.current = false;
                }
            }}
        >
            {showControls && (
                <div className="relative z-[10000] flex justify-end px-2 py-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="relative z-[10001] h-8 w-8 text-muted-foreground hover:text-foreground"
                        data-testid="chat-suggestions-refresh"
                        title="Refresh"
                        aria-label="Refresh"
                        onClick={handleRefresh}
                    >
                        <RefreshCw className="h-4 w-4" aria-hidden />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="relative z-[10001] h-8 w-8 text-muted-foreground hover:text-foreground"
                        data-testid="chat-suggestions-close"
                        title="Close"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        <X className="h-4 w-4" aria-hidden />
                    </Button>
                </div>
            )}
            {/* Die Vorschläge im Zuschnitt der beui-Karten: graue Fläche, weit
                gerundete Ecken, haarfeiner Rand — dieselbe Sprache wie die
                Ergebnis-Kacheln über der Eingabe. Die Kundenfarbe bleibt dem
                Sinnbild vorbehalten, die Fläche ist neutral.

                Die drei Karten stehen **immer**; solange die Vorschläge unterwegs
                sind, tragen sie blasse Balken statt Text. So bleibt die Höhe vom
                ersten Frame an dieselbe, und beim Eintreffen wechselt nur der
                Inhalt — kein zweiter Auftritt, der über den ersten läuft. */}
            {variant === 'list' ? (
                <div className="flex flex-col gap-0.5" data-testid="chat-suggestions-list">
                    {slots.map(slot => {
                        const suggestion = suggestions[slot];
                        const IconComponent = suggestion ? resolveSuggestionIcon(suggestion.icon) : null;
                        const iconColor = (suggestion as any)?.iconColor || '#666666';
                        return (
                            <div key={`suggestion-slot-${slot}`} className="relative min-h-11">
                                <AnimatePresence mode="popLayout" initial={false}>
                                    {!suggestion || !IconComponent ? (
                                        <motion.div key="empty" aria-hidden className="flex h-11 items-center gap-3 px-3" {...swap(slot)}>
                                            <span className="size-5 shrink-0 rounded-full bg-muted" />
                                            <span className="h-3 w-3/5 rounded bg-muted" />
                                        </motion.div>
                                    ) : (
                                        <motion.button
                                            key={suggestion.text}
                                            type="button"
                                            data-testid="chat-suggestion"
                                            onClick={() => onSuggestionClick(suggestion)}
                                            // `group/row`: the row is the hover/focus target of the marquee.
                                            className="group/row flex min-h-11 w-full min-w-0 items-center gap-3 rounded-xl px-3 py-2 text-left text-[15px] leading-6 text-foreground/90 transition-colors hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:bg-muted"
                                            {...swap(slot)}
                                        >
                                            <IconComponent aria-hidden className="size-5 shrink-0" style={{ color: iconColor, stroke: iconColor }} />
                                            {/* A cut-off suggestion marquees on hover/focus like a
                                                conversation title (29.09.2026); the key on the row
                                                remounts it on a swap, so a new text starts at rest. */}
                                            <MarqueeText
                                                as="span"
                                                text={suggestion.text}
                                                focusTrigger
                                                className="min-w-0 flex-1"
                                                testId="chat-suggestion-text"
                                            />
                                            {suggestion.toolLabel && (
                                                <span
                                                    className="max-w-[40%] shrink-0 truncate text-xs text-[var(--oa-fg-4)]"
                                                    title={suggestion.toolLabel}
                                                    data-testid="chat-suggestion-tool"
                                                >
                                                    {suggestion.toolLabel}
                                                </span>
                                            )}
                                        </motion.button>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                    {/* Manual rotation on the start screen: small and quiet under the rows —
                        only when there is another page to rotate to. */}
                    {skills.length > SUGGESTIONS_PER_PAGE && (
                    <button
                        type="button"
                        data-testid="chat-suggestions-more"
                        onClick={handleRefresh}
                        className="mt-1 self-center rounded-md px-3 py-1.5 text-xs text-[var(--oa-fg-4)] transition-colors hover:text-[var(--oa-fg-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                        {t('chatBotPage.moreSuggestions')}
                    </button>
                    )}
                </div>
            ) : (
            <div className="mb-3 grid gap-2 sm:grid-cols-3">
                {slots.map(slot => {
                    const suggestion = suggestions[slot];
                    const IconComponent = suggestion ? resolveSuggestionIcon(suggestion.icon) : null;
                    const iconColor = (suggestion as any)?.iconColor || '#666666';

                    return (
                        <div key={`suggestion-slot-${slot}`} className="relative h-full">
                            <AnimatePresence mode="popLayout" initial={false}>
                                {!suggestion || !IconComponent ? (
                                    <motion.div
                                        key="empty"
                                        aria-hidden
                                        className="flex h-full w-full flex-col items-start gap-3 rounded-2xl border border-border/60 bg-muted/30 p-4"
                                        {...swap(slot)}
                                    >
                                        <span className="size-8 shrink-0 rounded-full bg-background/60" />
                                        <span className="h-3 w-4/5 rounded bg-background/60" />
                                        <span className="h-3 w-2/5 rounded bg-background/60" />
                                    </motion.div>
                                ) : (
                                    <motion.button
                                        key={suggestion.text}
                                        type="button"
                                        data-testid="chat-suggestion"
                                        onClick={() => onSuggestionClick(suggestion)}
                                        className="flex h-full w-full flex-col items-start gap-3 rounded-2xl border border-border/60 bg-muted/50 p-4 text-left transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:bg-muted"
                                        {...swap(slot)}
                                    >
                                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-background/70">
                                            <IconComponent
                                                aria-hidden
                                                className="size-4"
                                                style={{ color: iconColor, stroke: iconColor }}
                                            />
                                        </span>
                                        <span className="line-clamp-3 text-sm leading-6 text-foreground/90">
                                            {suggestion.text}
                                        </span>
                                        {suggestion.toolLabel && (
                                            <span className="mt-auto truncate text-xs text-muted-foreground" data-testid="chat-suggestion-tool">
                                                {suggestion.toolLabel}
                                            </span>
                                        )}
                                    </motion.button>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
};
